import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase.js';

// Landing page for access-link emails using the token_hash flow. The email
// links here (…/auth/confirm?token_hash=…&type=email) instead of straight to
// Supabase's /auth/v1/verify endpoint, because institutional mail scanners
// prefetch every URL in an email — and the verify endpoint consumes the
// single-use token on GET, leaving the human with "otp_expired". A scanner
// will load this page but never click the button, so the token survives
// until the real user arrives.
//
// On success we persist the session and hard-reload, same as AuthCallback —
// see the CLAUDE.md note on why setSession + SPA navigate is not an option.
export default function AuthConfirm() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const tokenHash = params.get('token_hash');
  const type = params.get('type') || 'email';

  async function confirm() {
    setBusy(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        type,
        token_hash: tokenHash,
      });
      if (error) throw error;
      const s = data.session;
      if (!s) throw new Error('No session returned');
      const session = {
        access_token: s.access_token,
        refresh_token: s.refresh_token,
        expires_at: s.expires_at,
        expires_in: s.expires_in,
        token_type: s.token_type,
        user: data.user ?? s.user,
      };
      localStorage.setItem('hc.auth', JSON.stringify(session));
      window.location.replace('/');
    } catch {
      setFailed(true);
      setBusy(false);
    }
  }

  if (!tokenHash || failed) {
    return (
      <div className="mx-auto max-w-md px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-sm text-ink-secondary">{t('auth.confirm.invalid')}</p>
        <Link to="/auth" className="btn-primary mt-6 inline-block">
          {t('auth.confirm.requestNew')}
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-8 pb-20 pt-32 text-center">
      <h1 className="font-serif text-3xl text-gold-light">{t('auth.confirm.title')}</h1>
      <p className="mt-3 text-sm text-ink-secondary">{t('auth.confirm.body')}</p>
      <button
        type="button"
        className="btn-primary mt-6 disabled:cursor-not-allowed disabled:opacity-60"
        disabled={busy}
        onClick={confirm}
      >
        {busy ? t('auth.callback.title') : t('auth.confirm.button')}
      </button>
    </div>
  );
}
